/**
 * Saudi ERP — permissions modules, levels, and default roles.
 * Aligns with SAUDI_MODULE_STRUCTURE (15 modules).
 */
import { SAUDI_MODULE_STRUCTURE } from '../../products/saudi/config/saudiModuleStructure';

/** Permission levels (single level per module). */
export const PermissionLevels = {
  NONE: 'none',
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  FULL: 'full',
  /** @deprecated Legacy alias */
  SEE: 'view',
  /** @deprecated Legacy alias */
  READ: 'view',
  /** @deprecated Legacy alias */
  DOWNLOAD: 'view',
  /** @deprecated Legacy alias */
  ADD: 'create',
};

export const PERMISSION_LEVEL_RANK = {
  [PermissionLevels.NONE]: 0,
  [PermissionLevels.VIEW]: 1,
  [PermissionLevels.CREATE]: 2,
  [PermissionLevels.EDIT]: 3,
  [PermissionLevels.DELETE]: 4,
  [PermissionLevels.FULL]: 5,
};

/** Module keys — same ids as saudiModuleStructure. */
export const SystemSections = SAUDI_MODULE_STRUCTURE.reduce((acc, mod) => {
  const constName = mod.key || mod.id.toUpperCase();
  acc[constName.replace(/-/g, '_')] = mod.id;
  return acc;
}, {});

export const SAUDI_MODULE_KEYS = SAUDI_MODULE_STRUCTURE.map((m) => m.id);

export function getModuleDisplayNames() {
  return SAUDI_MODULE_STRUCTURE.reduce((acc, mod) => {
    acc[mod.id] = mod.label;
    return acc;
  }, {});
}

export function emptyModulePermissions(level = PermissionLevels.NONE) {
  return SAUDI_MODULE_KEYS.reduce((acc, key) => {
    acc[key] = level;
    return acc;
  }, {});
}

export function fullModulePermissions() {
  return emptyModulePermissions(PermissionLevels.FULL);
}

export function comparePermissionLevel(current, required) {
  const cur = PERMISSION_LEVEL_RANK[current] ?? 0;
  const req = PERMISSION_LEVEL_RANK[required] ?? 0;
  return cur >= req;
}

function withModules(overrides = {}, defaultLevel = PermissionLevels.NONE) {
  const base = emptyModulePermissions(defaultLevel);
  return { ...base, ...overrides };
}

/** Default Saudi role definitions. */
export const DEFAULT_SAUDI_ROLES = {
  admin: {
    nameKey: 'modules.shared.settingsBody.roles.admin',
    name: 'مدير النظام',
    descriptionKey: 'modules.shared.settingsBody.roles.admin_desc',
    description: 'صلاحيات كاملة على جميع الوحدات',
    permissions: fullModulePermissions(),
  },
  manager: {
    nameKey: 'modules.shared.settingsBody.roles.manager',
    name: 'مدير',
    descriptionKey: 'modules.shared.settingsBody.roles.manager_desc',
    description: 'إدارة على مستوى القسم أو الفريق',
    permissions: withModules(
      {
        accounting: PermissionLevels.VIEW,
        payroll: PermissionLevels.VIEW,
        settings: PermissionLevels.NONE,
      },
      PermissionLevels.FULL,
    ),
  },
  accountant: {
    nameKey: 'modules.shared.settingsBody.roles.accountant',
    name: 'محاسب',
    descriptionKey: 'modules.shared.settingsBody.roles.accountant_desc',
    description: 'المحاسبة والصندوق والبنك والفوترة',
    permissions: withModules(
      {
        articles: PermissionLevels.VIEW,
        inventory: PermissionLevels.VIEW,
        purchasing: PermissionLevels.VIEW,
        sales: PermissionLevels.VIEW,
        billing: PermissionLevels.FULL,
        crm: PermissionLevels.VIEW,
        accounting: PermissionLevels.FULL,
        cash: PermissionLevels.FULL,
        bank: PermissionLevels.FULL,
        approvals: PermissionLevels.VIEW,
        hr: PermissionLevels.NONE,
        employee_portal: PermissionLevels.NONE,
        attendance: PermissionLevels.VIEW,
        payroll: PermissionLevels.VIEW,
        settings: PermissionLevels.NONE,
      },
      PermissionLevels.NONE,
    ),
  },
  hr_manager: {
    nameKey: 'modules.shared.settingsBody.roles.hr_manager',
    name: 'مدير الموارد البشرية',
    descriptionKey: 'modules.shared.settingsBody.roles.hr_manager_desc',
    description: 'الموارد البشرية والحضور والرواتب',
    permissions: withModules(
      {
        hr: PermissionLevels.FULL,
        employee_portal: PermissionLevels.FULL,
        attendance: PermissionLevels.FULL,
        payroll: PermissionLevels.FULL,
        approvals: PermissionLevels.EDIT,
      },
      PermissionLevels.VIEW,
    ),
  },
  inventory_manager: {
    nameKey: 'modules.shared.settingsBody.roles.inventory_manager',
    name: 'مدير المخزون',
    descriptionKey: 'modules.shared.settingsBody.roles.inventory_manager_desc',
    description: 'الأصناف والمخزون والمشتريات',
    permissions: withModules(
      {
        articles: PermissionLevels.FULL,
        inventory: PermissionLevels.FULL,
        purchasing: PermissionLevels.FULL,
      },
      PermissionLevels.VIEW,
    ),
  },
  sales_manager: {
    nameKey: 'modules.shared.settingsBody.roles.sales_manager',
    name: 'مدير المبيعات',
    descriptionKey: 'modules.shared.settingsBody.roles.sales_manager_desc',
    description: 'المبيعات والفوترة وعلاقات العملاء',
    permissions: withModules(
      {
        sales: PermissionLevels.FULL,
        billing: PermissionLevels.FULL,
        crm: PermissionLevels.FULL,
      },
      PermissionLevels.VIEW,
    ),
  },
  employee: {
    nameKey: 'modules.shared.settingsBody.roles.employee',
    name: 'موظف',
    descriptionKey: 'modules.shared.settingsBody.roles.employee_desc',
    description: 'صلاحيات محدودة — بوابة الموظف والحضور',
    permissions: withModules(
      {
        employee_portal: PermissionLevels.FULL,
        attendance: PermissionLevels.VIEW,
        approvals: PermissionLevels.CREATE,
      },
      PermissionLevels.NONE,
    ),
  },
  view_only: {
    nameKey: 'modules.shared.settingsBody.roles.view_only',
    name: 'عرض فقط',
    descriptionKey: 'modules.shared.settingsBody.roles.view_only_desc',
    description: 'قراءة البيانات دون تعديل',
    permissions: emptyModulePermissions(PermissionLevels.VIEW),
  },
  /** Legacy alias — maps to admin */
  super_admin: {
    nameKey: 'modules.shared.settingsBody.roles.admin',
    name: 'مدير النظام',
    descriptionKey: 'modules.shared.settingsBody.roles.admin_desc',
    description: 'صلاحيات كاملة على جميع الوحدات',
    permissions: fullModulePermissions(),
  },
};

export default {
  PermissionLevels,
  SystemSections,
  SAUDI_MODULE_KEYS,
  DEFAULT_SAUDI_ROLES,
  getModuleDisplayNames,
  comparePermissionLevel,
};
